import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import RupiahInput from "@/components/RupiahInput";
import PPNBreakdown from "@/components/PPNBreakdown";
import api, { formatDetail } from "@/lib/api";
import SphSubmissionsList from "./SphSubmissionsList";

const emptyWO = { parent_project_id: "", wo_number: "", wo_title: "", work_description: "", wo_date: "", po_number: "", po_date: "", po_value: "", sph_submit_date: "", sph_submissions: [], site_survey_date: "", project_done_date: "", invoice_sent_date: "", retention_invoice_date: "", contract_value: "", retention_pct: 0, retention_period_days: 0, retention_type: "inside", is_ppn_exempt: false, is_urgent: false };
const toIso = (d) => (d ? new Date(d).toISOString() : null);

// 16-WO-SPH-DATE-UX-R1: derive latest SPH date for read-only "SPH Submit Date (latest)" field.
function pickLatestSphIso(list) {
  const arr = Array.isArray(list) ? list : [];
  let latest = "";
  for (const r of arr) {
    const d = (r?.date || "").trim();
    if (d && d > latest) latest = d;
  }
  return latest;
}
function woLatestSphDMY(list) {
  const iso = pickLatestSphIso(list);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export default function WorkingOrderForm({ outletParents, onDone, onCancel }) {
  const [wo, setWo] = useState(emptyWO);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!wo.parent_project_id) { toast.error("Please select a parent project"); return; }
    if (!wo.wo_number.trim()) { toast.error("WO Number is required"); return; }
    setBusy(true);
    try {
      await api.post("/active-projects", {
        project_type: "working_order",
        parent_project_id: wo.parent_project_id,
        wo_number: wo.wo_number,
        wo_title: (wo.wo_title || "").trim() || null,
        work_description: (wo.work_description || "").trim() || null,
        wo_date: toIso(wo.wo_date),
        po_number: wo.po_number || null,
        po_date: toIso(wo.po_date),
        po_value: wo.po_value ? parseFloat(wo.po_value) : null,
        // 149-cw-r14: Multi-SPH — kirim array kalau ada, fallback scalar.
        // Backend akan derive `sph_submit_date` = latest date otomatis.
        sph_submit_date: toIso(wo.sph_submit_date),
        sph_submissions: (wo.sph_submissions || []).filter((r) => r.date),
        site_survey_date: toIso(wo.site_survey_date),
        project_done_date: toIso(wo.project_done_date),
        invoice_sent_date: toIso(wo.invoice_sent_date),
        contract_value: wo.po_value ? parseFloat(wo.po_value) : null,
        retention_pct: parseFloat(wo.retention_pct) || 0,
        retention_period_days: parseInt(wo.retention_period_days, 10) || 0,
        retention_type: wo.retention_type || "inside",
        // 149-cw-r170: kategori WO (urgent/non-urgent) dipilih saat create.
        is_urgent: !!wo.is_urgent,
      });
      toast.success("Working Order created");
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="wo-form">
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Parent Project (Outlet)</Label>
        {/* 149-cw-r178: parent outlet bisa diketik utk mencari (tidak perlu scroll). */}
        <SearchableSelect
          testid="wo-parent-select"
          value={wo.parent_project_id}
          onChange={(v) => setWo({ ...wo, parent_project_id: v })}
          placeholder="Select a maintenance outlet…"
          searchPlaceholder="Ketik nama outlet…"
          emptyText="Belum ada outlet maintenance"
          options={outletParents.map((p) => ({ value: p.id, label: `${p.project_name}${p.client ? ` · ${p.client}` : ""}` }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">WO Number</Label>
          <Input data-testid="wo-number" required value={wo.wo_number} onChange={(e) => setWo({ ...wo, wo_number: e.target.value })} className="rounded-none" placeholder="e.g. WO-2026-001" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">WO Date</Label>
          <Input data-testid="wo-date" type="date" value={wo.wo_date} onChange={(e) => setWo({ ...wo, wo_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Judul WO <span className="text-slate-400 italic normal-case text-[10px]">(opsional)</span></Label>
        <Input data-testid="wo-title" value={wo.wo_title} onChange={(e) => setWo({ ...wo, wo_title: e.target.value })} className="rounded-none" placeholder="e.g. Renovasi Fasade Outlet Adam Malik" />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Work Description <span className="text-slate-400 italic normal-case text-[10px]">(deskripsi pekerjaan)</span></Label>
        <textarea
          data-testid="wo-work-description"
          value={wo.work_description}
          onChange={(e) => setWo({ ...wo, work_description: e.target.value })}
          rows={3}
          placeholder="Uraikan lingkup pekerjaan WO ini…"
          className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#FF5A1F] resize-y"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Kategori WO</Label>
        <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="wo-urgent-toggle">
          {[{ v: false, label: "Non Urgent Case" }, { v: true, label: "⚑ Urgent Case" }].map((o) => (
            <button key={String(o.v)} type="button" data-testid={o.v ? "wo-urgent-yes" : "wo-urgent-no"}
              onClick={() => setWo({ ...wo, is_urgent: o.v })}
              className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${wo.is_urgent === o.v ? (o.v ? "bg-rose-700 text-white" : "bg-[#0B1D3A] text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {o.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-500 italic">Sesuai pengelompokan kolom &quot;Kategori WO&quot; di Monitoring WO — pilih Urgent Case bila WO butuh perhatian khusus.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">PO Number</Label>
          <Input data-testid="wo-po-number" value={wo.po_number} onChange={(e) => setWo({ ...wo, po_number: e.target.value })} className="rounded-none" placeholder="e.g. PO-2026-001" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">PO Date</Label>
          <Input data-testid="wo-po-date" type="date" value={wo.po_date} onChange={(e) => setWo({ ...wo, po_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">PO Value (IDR)</Label>
          <Input data-testid="wo-po-value" type="number" min="0" step="1" value={wo.po_value} onChange={(e) => setWo({ ...wo, po_value: e.target.value })} className="rounded-none tabular-nums" placeholder="0" />
          <p className="text-[10px] text-slate-500 tabular-nums">
            {wo.po_value ? new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(parseFloat(wo.po_value) || 0) : ""}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Contract Value (IDR)</Label>
          <RupiahInput testid="wo-contract" value={wo.contract_value} onChange={(v) => setWo({ ...wo, contract_value: v })} placeholder="Same as PO value if empty" />
          <label className="flex items-center gap-1.5 text-[10px] cursor-pointer select-none mt-1" data-testid="wo-non-ppn-toggle">
            <input type="checkbox" checked={!!wo.is_ppn_exempt} onChange={(e) => setWo({ ...wo, is_ppn_exempt: e.target.checked })} className="h-3 w-3 accent-[#FF5A1F]" />
            <span className="uppercase tracking-wider font-semibold text-slate-600">Project Non-PPN (bebas PPN 11%)</span>
          </label>
          <PPNBreakdown value={wo.contract_value || wo.po_value} isExempt={!!wo.is_ppn_exempt} testid="wo-ppn-breakdown" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">SPH Submit Date (latest)</Label>
          {/* 16-WO-SPH-DATE-UX-R1: Read-only, auto-derived from Riwayat SPH. */}
          <div
            data-testid="wo-sph-latest-readonly"
            className="w-full h-9 border border-slate-200 bg-slate-50 px-3 flex items-center text-xs tabular-nums text-[#0B1D3A] font-semibold"
          >
            {woLatestSphDMY(wo.sph_submissions) || <span className="text-slate-400 italic font-normal">Belum ada SPH</span>}
          </div>
          <p className="text-[10px] text-slate-500 italic">Read-only — otomatis mengambil tanggal SPH terbaru dari Riwayat SPH di bawah.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Site Survey Date</Label>
          <Input data-testid="wo-survey-date" type="date" value={wo.site_survey_date} onChange={(e) => setWo({ ...wo, site_survey_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <SphSubmissionsList
        value={wo.sph_submissions}
        onChange={(next) => {
          const latest = pickLatestSphIso(next);
          setWo({ ...wo, sph_submissions: next, sph_submit_date: latest || wo.sph_submit_date || "" });
        }}
        testidPrefix="wo"
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Project Done Date</Label>
          <Input data-testid="wo-done-date" type="date" value={wo.project_done_date} onChange={(e) => setWo({ ...wo, project_done_date: e.target.value })} className="rounded-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Invoice Sent Date</Label>
          <Input data-testid="wo-invoice-date" type="date" value={wo.invoice_sent_date} onChange={(e) => setWo({ ...wo, invoice_sent_date: e.target.value })} className="rounded-none" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Retention Invoice Date <span className="text-slate-400 italic normal-case text-[10px]">(opsional)</span></Label>
        <Input data-testid="wo-retention-invoice-date" type="date" value={wo.retention_invoice_date} onChange={(e) => setWo({ ...wo, retention_invoice_date: e.target.value })} className="rounded-none" />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Retention</Label>
        <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="wo-retention">
          {[{ v: 0, label: "None" }, { v: 5, label: "5%" }, { v: 10, label: "10%" }].map((r) => (
            <button key={r.v} type="button" data-testid={`wo-retention-${r.v}`} onClick={() => setWo({ ...wo, retention_pct: r.v })}
              className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${wo.retention_pct === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {Number(wo.retention_pct) > 0 && (
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Jenis Retensi</Label>
          <div className="inline-flex border border-slate-300 text-xs w-full" data-testid="wo-retention-type">
            {[{ v: "inside", label: "Ditahan per penagihan" }, { v: "outside", label: "Ditahan pada akhir penagihan" }].map((r) => (
              <button key={r.v} type="button" data-testid={`wo-retention-type-${r.v}`}
                onClick={() => setWo({ ...wo, retention_type: r.v })}
                className={`flex-1 px-3 py-2 uppercase tracking-wider font-semibold ${(wo.retention_type || "inside") === r.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            <b className="text-[#0B1D3A]">Ditahan per penagihan</b>: retensi dipotong tiap invoice.<br/>
            <b className="text-[#0B1D3A]">Ditahan pada akhir penagihan</b>: retensi ditagih terpisah di invoice terakhir, tidak mengurangi Received Net per tagihan.
          </p>
        </div>
      )}
      {Number(wo.retention_pct) > 0 && (
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Retention Period (Masa Retensi)</Label>
          <div className="relative">
            <Input data-testid="wo-retention-period-days" type="number" min="0" max="3650" step="1"
              value={wo.retention_period_days ?? 0}
              onChange={(e) => setWo({ ...wo, retention_period_days: e.target.value })}
              className="rounded-none pr-14 tabular-nums" placeholder="0" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">Hari</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {[30, 90, 180, 365].map((d) => (
              <button key={d} type="button" data-testid={`wo-retention-preset-${d}`}
                onClick={() => setWo({ ...wo, retention_period_days: d })}
                className={`text-[10px] px-2 py-0.5 border transition-colors ${Number(wo.retention_period_days) === d ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"}`}>
                {d === 365 ? "1 Tahun" : `${d} Hari`}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500">
            Ketik durasi dalam <span className="font-semibold">hari</span> (0 = belum ditentukan).
            {Number(wo.retention_period_days) > 0 && (
              <span className="ml-1 text-amber-700 font-mono">≈ {(Number(wo.retention_period_days) / 30).toFixed(1)} bulan</span>
            )}
          </p>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" className="rounded-none" onClick={onCancel}>{ACTION_LABEL.cancel}</Button>
        <Button type="submit" data-testid="wo-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
          {busy ? "Saving…" : "Save Working Order"}
        </Button>
      </DialogFooter>
    </form>
  );
}
